import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, CalendarRange, RefreshCw, Loader2, Wallet, ChevronDown,
  ZoomIn, ZoomOut, ExternalLink,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { hexToRgba } from "../components/ColorPicker";
import { pastel, ink, entryHue } from "../lib/planningColors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The section's colour: coral red. Everything on this page derives from it,
// so changing the section colour is changing this line.
const ACCENT = "#E63946";
const ACCENT_WASH = hexToRgba(ACCENT, 0.06);

// The grid's header: the same wash, laid over white so the cell is opaque. The
// wash alone is 6% colour and 94% transparency, which looks the same on a cell
// that stays put and is wrong on the two that are pinned: a name scrolled
// sideways under CW and the day label showed straight through them, printed
// over their text.
const HEADER_FILL = {
  backgroundColor: "#FFFFFF",
  backgroundImage: `linear-gradient(${ACCENT_WASH}, ${ACCENT_WASH})`,
};

// The rail on the left: week number and date. Grey rather than white, so it
// reads as the edge of the grid rather than as a seventh person's column.
const RAIL = "#F1F5F9";
const RAIL_WEEKEND = "#E2E8F0";
const RAIL_WEEK = "#E9EEF4";

// The rules in the grid, named because getting them consistent is most of
// what makes it look regular. They are drawn on every cell without exception
// now, over the fills rather than between them: the colours run edge to edge
// and the lines sit on top, which is what a spreadsheet does.
const WEEK_RULE = "2px solid #94A3B8";
const DAY_RULE = "1px solid #CBD5E1";
const COL_RULE = "1px solid #CBD5E1";
const RAIL_RULE = "2px solid #94A3B8";

// Where the date column is pinned: the measured width of the week column beside
// it, written onto the table by the effect that observes that column. The
// fallback is the width w-12 asks for, which is what the first paint uses in
// the moment before the measurement lands.
const RAIL_OFFSET = "var(--rail-cw, 3rem)";

// How often the page re-asks while it is open. The server holds the workbook
// for two minutes, so most of these cost a cached reply.
const POLL_MS = 60000;

// Zoom. The page is installed as an app on phones, and in that mode the
// browser switches off two-finger zoom - not a setting of this page, a
// property of standalone mode - so the gesture is handled here instead.
//
// Every value here is a whole number of steps away from 1, and that is a
// constraint rather than a coincidence. The floor used to be 0.45: stepping
// down from 0.5 the clamp cut the step short and left the zoom at 45%, and
// from there every step up carried the remainder, so the ladder read 55, 65,
// 75 and eventually 105%. Anything that is not a multiple of the step is an
// odd number to show, and the ones above 1 are also where the frozen rail
// comes apart, which is a separate fault and not fixed by this.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.4;
const ZOOM_STEP = 0.1;
const ZOOM_PHONE = 0.6;
const PHONE_WIDTH = 640;

const STRINGS = {
  it: {
    title: "Planning", back: "Torna alla home", source: "Da SharePoint",
    updated: "aggiornato alle", refresh: "Aggiorna", loading: "Caricamento del planning\u2026",
    empty: "Nessuna attivit\u00e0 registrata in questo mese.", failed: "Planning non caricato",
    day: "Giorno", expenses: "Note spese", travel: "Viaggio", site: "Sito",
    monthEmpty: "Mese vuoto",
    noExpenses: "Nessuna nota spese registrata per questo mese",
    // Dice dove si va, non solo che si va da qualche parte: chi non ha i
    // permessi sulla raccolta riceve un rifiuto da Microsoft, e deve poter
    // capire che arriva da lì e non da Core.
    openSheet: "Apri il foglio su SharePoint",
    zoomIn: "Ingrandisci", zoomOut: "Riduci", zoomReset: "Dimensione normale",
  },
  en: {
    title: "Planning", back: "Back to home", source: "From SharePoint",
    updated: "updated at", refresh: "Refresh", loading: "Loading the planning\u2026",
    empty: "Nothing recorded for this month.", failed: "Planning not loaded",
    day: "Day", expenses: "Expenses", travel: "Travel", site: "Site",
    monthEmpty: "Empty month",
    noExpenses: "No expenses recorded for this month",
    openSheet: "Open the sheet on SharePoint",
    zoomIn: "Zoom in", zoomOut: "Zoom out", zoomReset: "Normal size",
  },
};

const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SHORT_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const SHORT_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DOT = "\u00b7";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * The second line of a cell: travel and site, in hours. Parsed values get an
 * h; a value that would not parse prints as typed - "10h30'", "NA" and a
 * column of "X" are all in the real sheet.
 */
function hoursLabel(entry, s) {
  const parts = [];
  const add = (label, value, raw) => {
    if (value != null) parts.push(`${label} ${value}h`);
    else if (raw) parts.push(`${label} ${raw}`);
  };
  add(s.travel, entry.travel, entry.travel_raw);
  add(s.site, entry.site, entry.site_raw);
  return parts.length ? parts.join(` ${DOT} `) : null;
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export default function Planning() {
  const { language } = useLanguage();
  const s = STRINGS[language] || STRINGS.it;
  const monthNames = language === "en" ? MONTHS_EN : MONTHS_IT;
  const shortNames = language === "en" ? SHORT_EN : SHORT_IT;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [monthIndex, setMonthIndex] = useState(() => new Date().getMonth());
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [zoom, setZoom] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < PHONE_WIDTH ? ZOOM_PHONE : 1
  );

  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const gridRef = useRef(null);
  const tableRef = useRef(null);
  const railRef = useRef(null);

  // Read by the loader, which is deliberately built once and kept: taking the
  // language as a dependency instead would rebuild it on every switch and fire
  // a fresh request at the server for nothing.
  const langRef = useRef(language);
  useEffect(() => { langRef.current = language; }, [language]);

  const silent = useRef(false);

  const load = useCallback(async (mode = "quiet") => {
    if (mode === "manual") setRefreshing(true);
    silent.current = mode === "poll";
    try {
      const { data: payload } = await axios.get(`${API}/planning`, {
        params: mode === "manual" ? { refresh: true } : {},
        withCredentials: true,
      });
      setData(payload);
    } catch (err) {
      // The server's own message when there is one, otherwise this page's, in
      // the language Core is set to. It used to read from the Italian table
      // whatever the language, so an English page announced its one failure in
      // Italian.
      const fallback = (STRINGS[langRef.current] || STRINGS.it).failed;
      if (!silent.current) toast.error(err.response?.data?.detail || fallback);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load("quiet"); }, [load]);

  useEffect(() => {
    const timer = setInterval(() => { if (!document.hidden) load("poll"); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) load("poll"); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  // Re-run on the month and on the payload, not only on loading. The element
  // these listeners are attached to exists only while the month on screen has
  // something in it: moving to an empty month removes it, and moving back
  // builds a new one. Watching loading alone, the listeners stayed on the
  // element that had been thrown away and the pinch was dead until the page
  // was reloaded.
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return undefined;
    let startDist = 0;
    let startZoom = 1;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onStart = (e) => {
      if (e.touches.length === 2) {
        startDist = dist(e.touches);
        startZoom = zoomRef.current;
      }
    };
    const onMove = (e) => {
      if (e.touches.length === 2 && startDist > 0) {
        e.preventDefault();
        setZoom(clamp(startZoom * (dist(e.touches) / startDist), ZOOM_MIN, ZOOM_MAX));
      }
    };
    const onEnd = () => { startDist = 0; };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [loading, monthIndex, data]);

  // Where the second frozen column starts.
  //
  // It has to be the rendered width of the first one, and that is not a number
  // this file gets to decide. The offset used to be written as left-12, three
  // rem, on the assumption that the week column is the 48px w-12 asks for. The
  // table is laid out automatically, so its real width is whatever the layout
  // algorithm arrives at: squeezed below 48 when the columns no longer fit the
  // page, and handed extra when they more than fit. Either way the date column
  // was then parked at a place the week column does not end, leaving a gap the
  // body scrolls through or an overlap that hides it.
  //
  // zoom makes it worse in one direction rather than causing it. The offset is
  // a length inside the zoomed subtree, so it is multiplied - measured at 48.8,
  // 51.2, 53.6 and 58.4 px for zoom 1, 1.05, 1.1 and 1.2 - while the column's
  // own width follows the layout, which zoom is squeezing at the same time. So
  // the two diverge as the zoom rises, and the point where it starts showing
  // depends on how many people the month has: with five the gap opens at 1.2,
  // with six it is already 10px at zoom 1.
  //
  // offsetWidth is the right quantity to copy and getBoundingClientRect is not:
  // measured across zoom 0.5 to 1.4, offsetWidth stays at 65 while the client
  // rect scales from 32.8 to 90.4. offsetWidth is in the same coordinates that
  // left is read in, which is what makes the two comparable without dividing by
  // the zoom by hand.
  //
  // Written straight onto the table as a custom property rather than kept in
  // state: this fires on every relayout, and re-rendering a month of rows to
  // move one offset would be a poor trade.
  useEffect(() => {
    const cell = railRef.current;
    const table = tableRef.current;
    if (!cell || !table) return undefined;

    let last = null;
    const apply = () => {
      const width = cell.offsetWidth;
      if (width === last) return;
      last = width;
      table.style.setProperty("--rail-cw", `${width}px`);
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(cell);
    return () => observer.disconnect();
  }, [loading, monthIndex, data]);

  const month = data?.months?.[monthIndex] || null;
  const people = data?.people || [];
  const today = todayISO();

  // How many rows each week covers, so the week number can be one merged cell
  // spanning its days rather than a badge on the first of them.
  const weekSpans = useMemo(() => {
    const out = {};
    if (!month) return out;
    let startDate = null;
    let count = 0;
    month.days.forEach((day, i) => {
      const isNew = i === 0 || month.days[i - 1].week !== day.week;
      if (isNew) {
        if (startDate) out[startDate] = count;
        startDate = day.date;
        count = 0;
      }
      count += 1;
    });
    if (startDate) out[startDate] = count;
    return out;
  }, [month]);

  const hasAnything = month?.days?.some((d) => Object.keys(d.entries || {}).length > 0);
  const withExpenses = month?.people?.filter((p) => p.expense != null) || [];

  if (loading) {
    return (
      <div className="min-h-screen">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-20 gap-3 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">{s.loading}</span>
          </div>
        </main>
      </div>
    );
  }

  const iconBtn = "w-9 h-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-500 transition-colors disabled:opacity-40";
  const tint = (e, on) => {
    e.currentTarget.style.color = on ? ACCENT : "";
    e.currentTarget.style.borderColor = on ? hexToRgba(ACCENT, 0.45) : "";
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: `linear-gradient(to bottom, ${hexToRgba(ACCENT, 0.08)} 0%, ${hexToRgba(ACCENT, 0.02)} 18%, rgba(255,255,255,0) 34%)`,
      }}
    >
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/"
          data-testid="planning-back"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {s.back}
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
          <div className="flex items-center gap-4 min-w-0">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: ACCENT, boxShadow: `0 10px 24px -8px ${hexToRgba(ACCENT, 0.6)}` }}
            >
              <CalendarRange className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
                {monthNames[monthIndex]} {data?.year}
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                {s.title} {DOT} {s.source}
                {data?.fetched_at && (
                  <> {DOT} {s.updated} {new Date(data.fetched_at).toLocaleTimeString(language, { hour: "2-digit", minute: "2-digit" })}</>
                )}
              </p>
              {/* Only when the server managed to ask SharePoint for it. The
                  address is the item's own, not a sharing link: it opens with
                  the account of whoever clicks, and shows them what their own
                  permissions allow. */}
              {data?.web_url && (
                <a
                  href={data.web_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="planning-open-sheet"
                  className="inline-flex items-center gap-1 text-xs font-semibold mt-1 hover:underline"
                  style={{ color: ACCENT }}
                >
                  {s.openSheet}
                  <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button type="button" onClick={() => setZoom((z) => clamp(z - ZOOM_STEP, ZOOM_MIN, ZOOM_MAX))} disabled={zoom <= ZOOM_MIN + 0.001} aria-label={s.zoomOut} className={iconBtn} onMouseEnter={(e) => tint(e, true)} onMouseLeave={(e) => tint(e, false)}>
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              title={s.zoomReset}
              className="h-9 min-w-[3.25rem] px-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold tabular-nums text-slate-600 transition-colors"
              onMouseEnter={(e) => tint(e, true)}
              onMouseLeave={(e) => tint(e, false)}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button type="button" onClick={() => setZoom((z) => clamp(z + ZOOM_STEP, ZOOM_MIN, ZOOM_MAX))} disabled={zoom >= ZOOM_MAX - 0.001} aria-label={s.zoomIn} className={iconBtn} onMouseEnter={(e) => tint(e, true)} onMouseLeave={(e) => tint(e, false)}>
              <ZoomIn className="w-4 h-4" />
            </button>

            <span className="w-px h-6 bg-slate-200 mx-1" />

            <button
              type="button"
              onClick={() => load("manual")}
              disabled={refreshing}
              data-testid="planning-refresh"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 transition-colors"
              onMouseEnter={(e) => tint(e, true)}
              onMouseLeave={(e) => tint(e, false)}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">{s.refresh}</span>
            </button>
          </div>
        </div>

        <div className="inline-flex max-w-full overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm mb-5">
          {(data?.months || []).map((m, i) => {
            const active = i === monthIndex;
            const filled = m.days?.some((d) => Object.keys(d.entries || {}).length > 0);
            return (
              <button
                key={m.sheet || i}
                type="button"
                onClick={() => setMonthIndex(i)}
                data-testid={`planning-month-${i}`}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  active ? "text-white shadow-sm" : filled ? "text-slate-600 hover:bg-slate-100" : "text-slate-300 hover:bg-slate-50"
                }`}
                style={active ? { backgroundColor: ACCENT } : undefined}
                title={filled ? undefined : s.monthEmpty}
              >
                {shortNames[i]}
              </button>
            );
          })}
        </div>

        {month?.error && <p className="text-sm text-red-600 mb-4">{month.error}</p>}

        <div className="rounded-2xl mb-5 shadow-sm overflow-hidden border bg-white" style={{ borderColor: hexToRgba(ACCENT, 0.3) }}>
          <button
            type="button"
            onClick={() => setExpensesOpen(!expensesOpen)}
            data-testid="planning-expenses-toggle"
            aria-expanded={expensesOpen}
            className="w-full flex items-center gap-2.5 px-5 py-3 text-left"
            style={{ backgroundColor: ACCENT_WASH }}
          >
            <Wallet className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
            <span className="text-xs uppercase tracking-widest font-bold" style={{ color: ACCENT }}>
              {month?.expense_label || s.expenses}
            </span>
            <ChevronDown className={`w-4 h-4 ml-auto shrink-0 transition-transform ${expensesOpen ? "rotate-180" : ""}`} style={{ color: ACCENT }} />
          </button>
          {expensesOpen && (
            <div className="px-5 py-4">
              {withExpenses.length === 0 ? (
                <p className="text-sm text-slate-400 text-center">{s.noExpenses}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {withExpenses.map((p) => (
                    <div key={p.name} className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 text-center min-w-0">
                      <p className="text-xs text-slate-500 truncate">{p.name}</p>
                      <p className="text-base font-bold text-slate-800 tabular-nums">
                        {p.expense.toLocaleString(language, { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-md overflow-hidden">
          {!hasAnything ? (
            <p className="text-center text-sm text-slate-400 py-16">{s.empty}</p>
          ) : (
            <div ref={gridRef} className="overflow-x-auto" style={{ touchAction: "pan-x pan-y" }}>
              <table ref={tableRef} className="w-full text-sm border-separate border-spacing-0" style={{ zoom }}>
                <thead>
                  <tr>
                    <th
                      ref={railRef}
                      className="sticky left-0 top-0 z-20 w-12 px-2 py-3 text-center text-[10px] font-bold uppercase tracking-wider"
                      style={{ ...HEADER_FILL, color: ACCENT, borderRight: COL_RULE, borderBottom: RAIL_RULE }}
                    >
                      CW
                    </th>
                    <th
                      className="sticky top-0 z-20 w-24 px-3 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-600"
                      style={{ ...HEADER_FILL, borderRight: RAIL_RULE, borderBottom: RAIL_RULE, left: RAIL_OFFSET }}
                    >
                      {s.day}
                    </th>
                    {people.map((name) => (
                      <th
                        key={name}
                        className="sticky top-0 z-10 px-2 py-3 text-center font-bold text-slate-800 whitespace-nowrap min-w-[11rem]"
                        style={{ ...HEADER_FILL, borderRight: COL_RULE, borderBottom: RAIL_RULE }}
                      >
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {month.days.map((day, i) => {
                    const weekend = day.weekday >= 5;
                    const isToday = day.date === today;
                    const newWeek = i === 0 || month.days[i - 1].week !== day.week;
                    const d = new Date(day.date);
                    const rowBg = weekend ? "#F8FAFC" : "#FFFFFF";
                    const railBg = weekend ? RAIL_WEEKEND : RAIL;

                    // One rule per row, the same on every column: the bar
                    // between weeks, or the line between days. No exceptions -
                    // that is what makes the grid regular, and with the fills
                    // running edge to edge a line over a continuous colour
                    // reads as a grid line rather than as a break. It was the
                    // white gap that made it read as a break, not the line.
                    const topRule = newWeek ? WEEK_RULE : DAY_RULE;

                    return (
                      // h-px lets a cell's contents take the row's height, so a
                      // fill reaches the rules above and below it.
                      <tr key={day.date} data-testid={`planning-day-${day.date}`} className="h-px">
                        {newWeek && (
                          <td
                            rowSpan={weekSpans[day.date] || 1}
                            className="sticky left-0 z-10 w-12 px-1 text-center align-middle"
                            style={{ backgroundColor: RAIL_WEEK, borderTop: WEEK_RULE, borderRight: COL_RULE }}
                          >
                            <span className="text-sm font-bold tabular-nums" style={{ color: ACCENT }}>
                              {day.week}
                            </span>
                          </td>
                        )}

                        <td
                          className="sticky z-10 px-2 py-1 text-center align-middle whitespace-nowrap"
                          style={{ backgroundColor: railBg, borderTop: topRule, borderRight: RAIL_RULE, left: RAIL_OFFSET }}
                        >
                          <span
                            className="inline-flex flex-col items-center justify-center w-12 h-10 rounded-lg leading-none"
                            style={isToday ? { backgroundColor: ACCENT, color: "#FFFFFF" } : undefined}
                          >
                            <span className={`text-base font-bold tabular-nums ${isToday ? "" : weekend ? "text-slate-400" : "text-slate-800"}`}>
                              {String(d.getDate()).padStart(2, "0")}
                            </span>
                            <span className={`text-[10px] uppercase tracking-wide ${isToday ? "opacity-80" : weekend ? "text-slate-400" : "text-slate-500"}`}>
                              {d.toLocaleDateString(language, { weekday: "short" }).replace(".", "")}
                            </span>
                          </span>
                        </td>

                        {people.map((name) => {
                          const entry = day.entries?.[name];

                          if (!entry) {
                            return (
                              <td
                                key={name}
                                style={{ backgroundColor: rowBg, borderTop: topRule, borderRight: COL_RULE }}
                              />
                            );
                          }

                          const hue = entryHue(entry);
                          const fill = pastel(hue);
                          const text = ink(hue);
                          const hours = hoursLabel(entry, s);

                          return (
                            // No padding on the cell and no rounding on the
                            // fill: the colour reaches all four edges, so the
                            // days of one trip touch and read as one band.
                            // What separates them is the rule drawn over the
                            // top, the same one every other cell gets.
                            <td
                              key={name}
                              className="p-0"
                              style={{ backgroundColor: fill, borderTop: topRule, borderRight: COL_RULE, height: "inherit" }}
                            >
                              <div
                                className="h-full min-h-[2.25rem] px-2 py-1.5 flex flex-col items-center justify-center text-center"
                                style={{ borderLeft: `3px solid ${hue}` }}
                              >
                                <div className="text-[13px] leading-tight font-semibold break-words" style={{ color: text }} title={entry.activity}>
                                  {entry.activity}
                                </div>
                                {hours && (
                                  <div className="text-[11px] leading-tight tabular-nums mt-0.5" style={{ color: text, opacity: 0.75 }}>
                                    {hours}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
